import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, Send, CheckCircle2, BarChart3 } from "lucide-react";
import { api, API } from "@/lib/api";
import { btn, gold, inp, ChannelIcon, fmtWhen, PostImages } from "./Queue";

const STATUS_LABEL = { ready: "Ready to post", approved: "Scheduled · auto-publishes", published: "Published", failed: "Publish failed" };

function MetricsForm({ post, onSaved }) {
  const [m, setM] = useState({ likes: post.metrics?.likes || 0, saves: post.metrics?.saves || 0, comments: post.metrics?.comments || 0 });
  const save = async () => {
    await api.post(`/admin/social/posts/${post.id}/metrics`, m);
    toast.success("Results logged");
    onSaved();
  };
  return (
    <div className="flex flex-wrap items-end gap-2 mt-3" data-testid={`metrics-form-${post.id}`}>
      {["likes", "saves", "comments"].map(k => (
        <label key={k} className="text-[11px] uppercase tracking-widest text-white/50">{k}
          <input type="number" min="0" value={m[k]} onChange={e => setM({ ...m, [k]: Number(e.target.value) })} className={`${inp} w-20 block mt-1`} data-testid={`metric-${k}-${post.id}`} />
        </label>
      ))}
      <button type="button" className={btn} onClick={save} data-testid={`metrics-save-${post.id}`}><BarChart3 size={12} /> Log results</button>
      {post.metrics?.orders != null && <span className="text-xs text-white/50 ml-2">{post.metrics.orders} order{post.metrics.orders === 1 ? "" : "s"} attributed</span>}
    </div>
  );
}

function ReadyCard({ post, tz, fbConnected, onChanged }) {
  const [fmt, setFmt] = useState("square");
  const copy = async () => {
    await navigator.clipboard.writeText(post.caption + (post.hashtags.length ? "\n\n" + post.hashtags.join(" ") : "") + (post.link_url ? "\n\n" + post.link_url : ""));
    toast.success("Caption copied");
  };
  const download = async () => {
    const r = await api.get(`/admin/social/posts/${post.id}/images.zip?fmt=${fmt}`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data); a.download = `post-${post.id.slice(0, 8)}-${fmt}.zip`; a.click();
    URL.revokeObjectURL(a.href);
  };
  const act = async (path, ok) => {
    try { await api.post(`/admin/social/posts/${post.id}/${path}`); toast.success(ok); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="border border-white/10 bg-white/[0.03] p-5" data-testid={`ready-post-${post.id}`}>
      <div className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-widest text-white/50 mb-3">
        <span className="inline-flex items-center gap-1 text-white"><ChannelIcon c={post.channel} /> {post.channel}</span>
        <span>· {post.theme_name}</span><span>· {fmtWhen(post.scheduled_at, tz)}</span>
        <span className={post.status === "published" ? "text-emerald-300" : post.status === "failed" ? "text-red-400" : "text-[#C77746]"} data-testid={`status-${post.id}`}>· {STATUS_LABEL[post.status] || post.status}</span>
      </div>
      <div className="grid md:grid-cols-[1fr_240px] gap-6">
        <p className="text-sm text-white/80 whitespace-pre-wrap leading-relaxed" data-testid={`ready-caption-${post.id}`}>{post.caption}{post.hashtags.length ? "\n\n" + post.hashtags.join(" ") : ""}</p>
        <PostImages post={post} size="w-16 h-16" />
      </div>
      {post.error && <div className="text-xs text-red-400 mt-2">{post.error}</div>}
      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/10 items-center">
        <button type="button" className={btn} onClick={copy} data-testid={`copy-${post.id}`}><Copy size={12} /> Copy caption{post.hashtags.length ? " + hashtags" : ""}</button>
        <select value={fmt} onChange={e => setFmt(e.target.value)} className={inp} data-testid={`zip-format-${post.id}`}>
          <option value="square">1080×1080 square</option><option value="portrait">1080×1350 portrait</option><option value="story">1080×1920 story/reel</option>
        </select>
        <button type="button" className={btn} onClick={download} data-testid={`download-${post.id}`}><Download size={12} /> Download images</button>
        {post.status !== "published" && post.channel === "facebook" && fbConnected && <button type="button" className={gold} onClick={() => act("publish", "Published to Facebook")} data-testid={`publish-now-${post.id}`}><Send size={12} /> Publish now</button>}
        {post.status !== "published" && <button type="button" className={btn} onClick={() => act("mark-posted", "Marked as posted")} data-testid={`mark-posted-${post.id}`}><CheckCircle2 size={12} /> I posted it</button>}
      </div>
      {post.status === "published" && (post.channel === "instagram" || !post.fb_post_id) && <MetricsForm post={post} onSaved={onChanged} />}
      {post.status === "published" && post.fb_post_id && post.metrics && (
        <div className="text-xs text-white/50 mt-3" data-testid={`fb-metrics-${post.id}`}>Facebook: {post.metrics.likes || 0} likes · {post.metrics.comments || 0} comments · {post.metrics.shares || 0} shares · {post.metrics.clicks || 0} link clicks · {post.metrics.orders || 0} orders</div>
      )}
    </div>
  );
}

export function Ready({ settings, reloadKey }) {
  const [posts, setPosts] = useState([]);
  const [tab, setTab] = useState("ready,approved,failed");
  const load = () => api.get(`/admin/social/posts?status=${tab}`).then(r => setPosts(r.data));
  useEffect(() => { load(); }, [tab, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="social-ready">
      <div className="flex gap-2 mb-6">
        {[["ready,approved,failed", "Ready to Post"], ["published", "Published"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} className={`${btn} ${tab === k ? "border-[#C77746] text-[#C77746]" : ""}`} data-testid={`ready-tab-${k.split(",")[0]}`}>{l}</button>
        ))}
      </div>
      {posts.length === 0 && <p className="text-white/40 text-sm" data-testid="ready-empty">Nothing here yet.</p>}
      <div className="space-y-5">{posts.map(p => <ReadyCard key={p.id} post={p} tz={settings.timezone} fbConnected={settings.fb_connected} onChanged={load} />)}</div>
    </div>
  );
}

export { API };
